import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import * as React from 'react';

import type { RouterOutputs } from '~/lib/trpc';
import { useTRPC } from '~/lib/trpc';
import { cn } from '~/lib/utils';

export type RecipientSuggestion =
  RouterOutputs['contacts']['addressBook']['suggestAddresses'][number];

/** One letter matches half the address book, and every keystroke is a query. */
const MIN_QUERY_LENGTH = 2;

/** Long enough that a typed word is one query, short enough to feel live. */
const SUGGESTION_DEBOUNCE_MS = 200;

/**
 * The saved addresses worth offering for what is currently typed.
 *
 * Only owned addresses come back, and the ones already on the field are
 * dropped here rather than server-side, since the server has no idea what the
 * draft already holds.
 */
export function useRecipientSuggestions({
  query,
  recipients,
  enabled,
}: {
  query: string;
  recipients: Array<string>;
  enabled: boolean;
}): Array<RecipientSuggestion> {
  const trpc = useTRPC();
  const search = useDebounce(query.trim(), SUGGESTION_DEBOUNCE_MS);
  const isEnabled = enabled && search.length >= MIN_QUERY_LENGTH;
  const { data } = useQuery({
    ...trpc.contacts.addressBook.suggestAddresses.queryOptions({ search }),
    enabled: isEnabled,
  });

  return React.useMemo(() => {
    if (!isEnabled || !data) {
      return [];
    }

    const taken = new Set(
      recipients.map((recipient) => recipient.trim().toLowerCase()),
    );

    return data.filter((suggestion) => !taken.has(suggestion.email));
  }, [data, isEnabled, recipients]);
}

/**
 * The list itself. A plain listbox rather than a `dropdown-menu`: the menu
 * primitive moves focus to the highlighted item, and here focus has to stay in
 * the input so typing never stops.
 */
export function RecipientSuggestionList({
  suggestions,
  activeIndex,
  listId,
  optionId,
  onActivate,
  onSelect,
}: {
  suggestions: Array<RecipientSuggestion>;
  activeIndex: number;
  listId: string;
  optionId: (index: number) => string;
  onActivate: (index: number) => void;
  onSelect: (suggestion: RecipientSuggestion) => void;
}) {
  return (
    <ul
      id={listId}
      role="listbox"
      className="absolute left-0 top-full z-50 mt-1 max-h-56 w-72 max-w-full overflow-y-auto rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md"
    >
      {suggestions.map((suggestion, index) => (
        <li
          key={suggestion.email}
          id={optionId(index)}
          role="option"
          aria-selected={index === activeIndex}
          /** Mousedown would blur the input first, and a blur adds whatever is
           *  half-typed — the partial address, not the one being clicked. */
          onMouseDown={(event) => event.preventDefault()}
          onMouseMove={() => onActivate(index)}
          onClick={() => onSelect(suggestion)}
          className={cn(
            'flex cursor-pointer flex-col rounded-sm px-2 py-1.5 text-xs',
            index === activeIndex && 'bg-accent text-accent-foreground',
          )}
        >
          <span className="truncate font-medium">{suggestion.displayName}</span>
          {suggestion.displayName !== suggestion.email && (
            <span className="truncate text-muted-foreground">
              {suggestion.email}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}
